/* eslint-disable react-hooks/exhaustive-deps */
import React,{useEffect,useMemo,useState} from 'react';
import {useNavigate,useLocation,useSearchParams} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import DataTable from '../components/common/DataTable';
import SlidePanel from '../components/common/SlidePanel';
import {Input,Select,Textarea} from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';
import {downloadCSV} from '../utils/exportUtils';
import {prettyMortuary} from '../utils/mortuaryLabels';

const unwrap=(r:any)=>r?.data?.data??r?.data??r;
const TABS=[
  ['dashboard','Command centre'],['intake','Intake queue'],['register','Register body'],['records','Register'],
  ['search','Search'],['chambers','Chambers'],['postmortem','Autopsy'],['unclaimed','Unclaimed'],
  ['releases','Releases'],['transfers','Transfers'],['belongings','Belongings'],['mci','Mass casualty'],
  ['reports','Reports'],['analytics','Analytics'],['masters','Masters'],
] as const;
const TAB_IDS=TABS.map(([id])=>id);
const tabFromPath=(pathname:string)=>{
  const part=pathname.replace(/^\/mortuary\/?/,'').split('/')[0];
  if(!part||part==='dashboard') return 'dashboard' as (typeof TABS)[number][0];
  return (TAB_IDS as readonly string[]).includes(part)?part as (typeof TABS)[number][0]:'dashboard';
};
const CLOSED=new Set(['RELEASED','TRANSFERRED','DISPOSED','FINAL_DISPOSITION_COMPLETED']);
const QUEUES=[
  ['in-process','In process'],
  ['awaiting-id','Awaiting ID'],
  ['awaiting-storage','Awaiting storage'],
  ['autopsy','Autopsy'],
  ['ready-release','Ready to release'],
  ['hold','Legal hold'],
  ['unclaimed','Unclaimed'],
  ['released','Released'],
  ['transferred','Transferred'],
  ['closed','Closed'],
  ['all','All bodies'],
] as const;
const QUEUE_META:Record<string,{label:string;cls:string}>={
  'in-process':{label:'In process',cls:'bg-sky-50 text-sky-800 border-sky-200'},
  'awaiting-id':{label:'Awaiting ID',cls:'bg-amber-50 text-amber-800 border-amber-200'},
  'awaiting-storage':{label:'Awaiting storage',cls:'bg-orange-50 text-orange-800 border-orange-200'},
  autopsy:{label:'Autopsy',cls:'bg-violet-50 text-violet-800 border-violet-200'},
  'ready-release':{label:'Ready to release',cls:'bg-emerald-50 text-emerald-800 border-emerald-200'},
  hold:{label:'Legal hold',cls:'bg-red-50 text-red-800 border-red-200'},
  unclaimed:{label:'Unclaimed',cls:'bg-pink-50 text-pink-800 border-pink-200'},
  released:{label:'Released',cls:'bg-slate-100 text-slate-600 border-slate-200'},
  transferred:{label:'Transferred',cls:'bg-indigo-50 text-indigo-800 border-indigo-200'},
  closed:{label:'Closed',cls:'bg-slate-50 text-slate-500 border-slate-200'},
};
function queueOf(b:any){
  const st=String(b.status||'');
  if(st==='RELEASED') return 'released';
  if(st==='TRANSFERRED') return 'transferred';
  if(st==='DISPOSED'||st==='FINAL_DISPOSITION_COMPLETED') return 'closed';
  if(b.legal_hold) return 'hold';
  if(st==='UNCLAIMED') return 'unclaimed';
  const id=String(b.identification_status||'');
  if(st==='IDENTIFICATION_PENDING'||['UNIDENTIFIED','UNKNOWN','PARTIALLY_IDENTIFIED'].includes(id)) return 'awaiting-id';
  if(!b.current_chamber_id&&!b.chamber_number) return 'awaiting-storage';
  if(b.post_mortem_required&&st!=='POST_MORTEM_COMPLETED'&&!st.includes('AUTOPSY_COMPLETED')) return 'autopsy';
  if(st==='RELEASE_PENDING'||st==='POST_MORTEM_COMPLETED'||st==='STORAGE_ASSIGNED'||st==='IDENTIFIED') return 'ready-release';
  return 'in-process';
}
function matchesQueue(b:any,q:string){
  if(!q||q==='all') return true;
  if(q==='in-process') return !CLOSED.has(String(b.status||''));
  if(['awaiting-id','awaiting-storage','autopsy','hold','released','ready-release'].includes(q)) return laneMatch(b,q);
  return queueOf(b)===q;
}
function laneMatch(b:any,q:string){
  const st=String(b.status||'');
  const closed=CLOSED.has(st);
  if(q==='released') return st==='RELEASED';
  if(q==='hold') return !!b.legal_hold&&!closed;
  if(q==='awaiting-id') return !closed&&(st==='IDENTIFICATION_PENDING'||['UNIDENTIFIED','UNKNOWN','PARTIALLY_IDENTIFIED'].includes(String(b.identification_status||'')));
  if(q==='awaiting-storage') return !closed&&!b.current_chamber_id&&!b.chamber_number;
  if(q==='autopsy') return !closed&&!!b.post_mortem_required&&st!=='POST_MORTEM_COMPLETED'&&!String(st).includes('AUTOPSY_COMPLETED');
  if(q==='ready-release') return !closed&&queueOf(b)==='ready-release';
  return matchesQueue(b,q);
}
function QueueBadge({body}:{body:any}){
  const q=queueOf(body);
  const m=QUEUE_META[q]||QUEUE_META['in-process'];
  return <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
}
const PAGE_HELP:Record<string,string>={
  dashboard:'Start here. Cards and work lanes show who is waiting, who is ready to release, and who is already released.',
  intake:'Hospital deaths not yet in the mortuary. Register them to start a body record.',
  register:'Create a new body record. You can assign a chamber now or later.',
  records:'Daily list. In process is the default so released cases do not hide active work. Click a row to continue work.',
  search:'Find a body by name, body number, case number, or ID.',
  chambers:'See which tray is free and who is stored in each chamber. Click a name to open that body.',
  postmortem:'Bodies that still need autopsy / post-mortem.',
  unclaimed:'Bodies held past the facility unclaimed threshold. Final disposition is never automatic.',
  releases:'Bodies already handed over.',
  transfers:'Bodies moved to another facility, funeral home, or destination.',
  belongings:'Personal items recorded against a body.',
  mci:'Mass-casualty intake — create several unidentified records at once.',
  reports:'Printable operational lists.',
  analytics:'Counts and open SLA items.',
  masters:'Chamber and lookup setup.',
};
function occupantsOf(ch:any, bodies:any[]){
  const raw=ch?.occupants;
  if(Array.isArray(raw)&&raw.length) return raw;
  if(typeof raw==='string'){
    try{const p=JSON.parse(raw); if(Array.isArray(p)&&p.length) return p;}catch{/* ignore */}
  }
  return (bodies||[]).filter((b:any)=>b.current_chamber_id===ch.id&&!CLOSED.has(String(b.status||'')));
}
function reportColumns(rows:any[]){
  const keys=Object.keys(rows[0]||{body_number:'',full_name:'',status:''});
  const hide=new Set(['id','body_id','patient_id','visit_id','encounter_id','admission_id','scan_token','facility_id','mortuary_id','created_by','updated_by','created_at','updated_at','current_chamber_id']);
  const preferred=['body_number','case_number','full_name','status','identification_status','chamber_number','received_at','released_at','recipient_name','destination','item_description','kind','plot_code','trip_number','severity','message'];
  const shown=preferred.filter(k=>keys.includes(k));
  const extra=keys.filter(k=>!shown.includes(k)&&!hide.has(k));
  return [...shown,...extra].slice(0,8).map(k=>({
    key:k,
    label:k.replace(/_/g,' ').replace(/\b\w/g,(c:string)=>c.toUpperCase()),
    render:(r:any)=>{
      const v=r[k];
      if(['status','identification_status','lifecycle_status'].includes(k)) return prettyMortuary(v);
      if(String(k).endsWith('_at')) return fmtDateTime(v);
      return v??'—';
    },
  }));
}
function chamberTone(ch:any, occ:any[]){
  const st=String(ch.status||'');
  if(st==='OUT_OF_SERVICE'||st==='MAINTENANCE') return {cls:'border-slate-200 bg-slate-50',label:'Offline'};
  if(st==='RESERVED') return {cls:'border-indigo-200 bg-indigo-50',label:'Reserved'};
  if(occ.length>=(ch.capacity||1)||st==='OCCUPIED') return {cls:'border-rose-200 bg-rose-50',label:'Occupied'};
  return {cls:'border-emerald-200 bg-emerald-50',label:'Free'};
}

export default function MortuaryPage(){
  const nav=useNavigate();
  const loc=useLocation();
  const [params]=useSearchParams();
  const [tab,setTab]=useState<(typeof TABS)[number][0]>(()=>tabFromPath(loc.pathname));
  const queue=params.get('queue')||(tab==='records'?'in-process':'all');
  const goTab=(id:(typeof TABS)[number][0])=>{
    setTab(id);
    if(id==='records') nav('/mortuary/records?queue=in-process');
    else nav(id==='dashboard'?'/mortuary':`/mortuary/${id}`);
  };
  const goQueue=(q:string)=>nav(`/mortuary/records?queue=${q}`);
  const [msg,setMsg]=useState('');
  const [loading,setLoading]=useState(false);
  const [dash,setDash]=useState<any>({stats:{},recentAdmissions:[],recentReleases:[],alerts:[]});
  const [lookups,setLookups]=useState<any>({});
  const [filters,setFilters]=useState({search:'',status:'',from:'',to:''});
  const [bodies,setBodies]=useState<any[]>([]);
  const [chambers,setChambers]=useState<any[]>([]);
  const [mortuaries,setMortuaries]=useState<any[]>([]);
  const [unclaimed,setUnclaimed]=useState<any[]>([]);
  const [reportKind,setReportKind]=useState('in-mortuary');
  const [reportRows,setReportRows]=useState<any[]>([]);
  const [settings,setSettings]=useState<any>({});
  const [patient,setPatient]=useState<any>(null);
  const [saving,setSaving]=useState(false);
  const [form,setForm]=useState<any>({fullName:'',gender:'',source:'INPATIENT',deathType:'NATURAL',bodyCondition:'GOOD',postMortemRequired:false,viewingAllowed:true});
  const [chamberForm,setChamberForm]=useState<any>({chamberNumber:'',rackShelfSlot:'',capacity:1,status:'AVAILABLE',temperature:'2-4°C'});
  const [showChamber,setShowChamber]=useState(false);
  const [intakes,setIntakes]=useState<any>({ipd:[],emergency:[]});
  const [belongingRows,setBelongingRows]=useState<any[]>([]);
  const [transferRows,setTransferRows]=useState<any[]>([]);
  const [tempChamber,setTempChamber]=useState('');
  const [tempC,setTempC]=useState('');
  const [tempLogs,setTempLogs]=useState<any[]>([]);
  const [chamberFilter,setChamberFilter]=useState<'all'|'free'|'occupied'|'offline'>('all');
  const [moreStats,setMoreStats]=useState(false);
  const [mci,setMci]=useState<any[]>([]);
  const [mciName,setMciName]=useState('');
  const [mciCount,setMciCount]=useState(3);
  const [searchRows,setSearchRows]=useState<any[]>([]);
  const [exec,setExec]=useState<any>(null);
  const [slaRows,setSlaRows]=useState<any[]>([]);

  const notify=(m:string)=>setMsg(m);
  const opts=(type:string)=>lookups[type]||[];

  const loadLookups=async()=>{
    const [l,m,s]=await Promise.all([api.getMortuaryLookups(),api.getMortuaries(),api.getMortuarySettings()]);
    setLookups(unwrap(l)||{});
    setMortuaries(unwrap(m)||[]);
    setSettings(unwrap(s)||{});
  };

  const loadDash=async()=>{
    setLoading(true);
    try{
      const r=await api.getMortuaryDashboard(filters);
      setDash(unwrap(r)||{stats:{},recentAdmissions:[],recentReleases:[],alerts:[]});
    }catch(e:any){notify(e.response?.data?.message||'Could not load dashboard');}
    finally{setLoading(false);}
  };

  const loadBodies=async()=>{
    setLoading(true);
    try{
      const r=await api.getMortuaryBodies({...filters,take:100});
      const body=unwrap(r);
      setBodies(body?.data||body||[]);
    }catch(e:any){notify(e.response?.data?.message||'Could not load records');}
    finally{setLoading(false);}
  };

  const loadChambers=async()=>{
    const r=await api.getMortuaryChambers();
    setChambers(unwrap(r)||[]);
  };

  const loadUnclaimed=async()=>{
    const r=await api.getMortuaryUnclaimed();
    setUnclaimed(unwrap(r)||[]);
  };

  const loadIntakes=async()=>{
    const r=await api.getMortuaryPendingIntakes();
    setIntakes(unwrap(r)||{ipd:[],emergency:[]});
  };

  const loadBelongings=async()=>{
    const r=await api.getMortuaryReport('belongings');
    const rows=unwrap(r);
    setBelongingRows(Array.isArray(rows)?rows:(rows?.data||[]));
  };

  const loadTransfers=async()=>{
    const r=await api.getMortuaryReport('transfers');
    const rows=unwrap(r);
    setTransferRows(Array.isArray(rows)?rows:(rows?.data||[]));
  };

  const loadTemp=async(chamberId:string)=>{
    if(!chamberId){setTempLogs([]);return;}
    const r=await api.getMortuaryChamberTemp(chamberId);
    setTempLogs(unwrap(r)||[]);
  };

  const loadReport=async()=>{
    const r=await api.getMortuaryReport(reportKind,filters);
    const rows=unwrap(r);
    setReportRows(Array.isArray(rows)?rows:(rows?.data||[]));
  };

  useEffect(()=>{loadLookups();},[]);
  useEffect(()=>{setTab(tabFromPath(loc.pathname));},[loc.pathname]);
  useEffect(()=>{
    if(tab==='dashboard') {loadDash();loadExec();loadBodies();}
    if(tab==='records'||tab==='releases'||tab==='postmortem') loadBodies();
    if(tab==='chambers'||tab==='register'||tab==='masters') {loadChambers(); if(tab==='chambers') loadBodies();}
    if(tab==='unclaimed') loadUnclaimed();
    if(tab==='intake') loadIntakes();
    if(tab==='belongings') loadBelongings();
    if(tab==='transfers') loadTransfers();
    if(tab==='reports') loadReport();
    if(tab==='mci') api.getMortuaryMci().then(r=>setMci(unwrap(r)||[])).catch(()=>null);
    if(tab==='search') loadSearch();
    if(tab==='analytics') loadExec();
  },[tab]);

  const loadSearch=async()=>{
    setLoading(true);
    try{
      const r=await api.searchMortuary({...filters,take:100});
      const body=unwrap(r);
      setSearchRows(body?.data||body||[]);
    }catch(e:any){notify(e.response?.data?.message||'Search failed');}
    finally{setLoading(false);}
  };

  const loadExec=async()=>{
    try{
      const [a,s]=await Promise.all([api.getMortuaryExecutive(),api.getMortuarySla()]);
      setExec(unwrap(a)||{});
      setSlaRows(unwrap(s)||[]);
    }catch(e:any){notify(e.response?.data?.message||'Analytics failed');}
  };

  const register=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      const r=await api.registerMortuaryBody({
        ...form,
        patientId:patient?.id||null,
        medicalRecordNumber:form.medicalRecordNumber||patient?.uhid||'',
        fullName:form.fullName||[patient?.firstName,patient?.lastName].filter(Boolean).join(' '),
        gender:form.gender||patient?.gender||'',
        dateOfBirth:form.dateOfBirth||patient?.dateOfBirth||null,
        nationalId:form.nationalId||patient?.nationalId||patient?.idNumber||'',
      });
      const saved=unwrap(r);
      notify(`✅ Registered ${saved.body_number||saved.bodyNumber}`);
      setForm({fullName:'',gender:'',source:'INPATIENT',deathType:'NATURAL',bodyCondition:'GOOD',postMortemRequired:false,viewingAllowed:true});
      setPatient(null);
      nav(`/mortuary/bodies/${saved.id}`);
    }catch(ex:any){notify('❌ '+(ex.response?.data?.message||'Registration failed'));}
    finally{setSaving(false);}
  };

  const saveChamber=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      await api.saveMortuaryChamber({...chamberForm,mortuaryId:chamberForm.mortuaryId||mortuaries[0]?.id});
      notify('✅ Chamber saved');setShowChamber(false);await loadChambers();
    }catch(ex:any){notify('❌ '+(ex.response?.data?.message||'Could not save chamber'));}
    finally{setSaving(false);}
  };

  const stats=dash.stats||{};
  const queueCounts=useMemo(()=>{
    const c:Record<string,number>={all:bodies.length};
    for(const [id] of QUEUES){
      if(id==='all') continue;
      c[id]=bodies.filter(b=>matchesQueue(b,id)).length;
    }
    return c;
  },[bodies]);
  const queuedBodies=useMemo(()=>bodies.filter(b=>matchesQueue(b,queue)),[bodies,queue]);
  const laneBodies=(q:string)=>bodies.filter(b=>laneMatch(b,q)).slice(0,6);
  const laneCount=(q:string)=>bodies.filter(b=>laneMatch(b,q)).length;
  return(
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Mortuary</h1>
            <p className="text-xs text-slate-500">See who is in the mortuary, who still needs identification, and who is ready to release.</p>
            <div className="flex flex-wrap gap-3">
              <button className="text-xs text-[#02357A] underline" onClick={()=>goTab('register')}>Register body</button>
              <button className="text-xs text-[#02357A] underline" onClick={()=>goTab('intake')}>Intake queue</button>
              <button className="text-xs text-[#02357A] underline" onClick={()=>nav('/mortuary/iot')}>Cold-storage IoT</button>
              <button className="text-xs text-[#02357A] underline" onClick={()=>nav('/mortuary/chapel')}>Chapel</button>
              <button className="text-xs text-[#02357A] underline" onClick={()=>nav('/mortuary/transport')}>Transport</button>
              <button className="text-xs text-[#02357A] underline" onClick={()=>nav('/mortuary/cemetery')}>Cemetery</button>
              <button className="text-xs text-[#02357A] underline" onClick={()=>nav('/mortuary/crematorium')}>Crematorium</button>
            </div>
          </div>
        </div>
        {msg&&<div className="text-sm bg-slate-50 border rounded-lg px-3 py-2">{msg}</div>}
        <div className="flex flex-wrap gap-2">
          {TABS.map(([id,label])=>(
            <button key={id} onClick={()=>goTab(id)} className={`text-xs px-3 py-1.5 rounded-full border ${tab===id?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600'}`}>{label}</button>
          ))}
        </div>
        {PAGE_HELP[tab]&&<p className="text-sm text-slate-600 bg-sky-50 border border-sky-100 rounded-xl px-4 py-2">{PAGE_HELP[tab]}</p>}

        {(tab==='records'||tab==='reports'||tab==='search')&&(
          <div className="card p-3 grid grid-cols-2 lg:grid-cols-5 gap-2">
            <Input placeholder="Search name, body no, case or ID" value={filters.search} onChange={e=>setFilters({...filters,search:e.target.value})}/>
            <Select value={filters.status} onChange={e=>setFilters({...filters,status:e.target.value})}>
              <option value="">All statuses</option>
              {['RECEIVED','IDENTIFICATION_PENDING','IDENTIFIED','STORAGE_ASSIGNED','POST_MORTEM_PENDING','POST_MORTEM_COMPLETED','RELEASE_PENDING','RELEASED','TRANSFERRED','UNCLAIMED','DISPOSED'].map(s=><option key={s} value={s}>{prettyMortuary(s)}</option>)}
            </Select>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Received from</label>
              <Input type="date" value={filters.from} onChange={e=>setFilters({...filters,from:e.target.value})}/>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Received to</label>
              <Input type="date" value={filters.to} onChange={e=>setFilters({...filters,to:e.target.value})}/>
            </div>
            <button className="btn-primary text-sm self-end" onClick={()=>{if(tab==='reports')loadReport(); else if(tab==='search')loadSearch(); else loadBodies();}}>Apply</button>
          </div>
        )}

        {tab==='dashboard'&&(
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-3">
              {([
                ['In mortuary now',stats.inMortuary,'blue',()=>goQueue('in-process')],
                ['Need identification',stats.awaitingIdentification,'amber',()=>goQueue('awaiting-id')],
                ['Ready to release',stats.awaitingRelease,'purple',()=>goQueue('ready-release')],
                ['Need autopsy',stats.awaitingPostMortem,'orange',()=>goTab('postmortem')],
                ['Free chambers',stats.chambersAvailable,'cyan',()=>goTab('chambers')],
                ['Occupied chambers',stats.chambersOccupied,'red',()=>goTab('chambers')],
                ['Released today',stats.releasedToday,'green',()=>goQueue('released')],
                ['Unclaimed / overdue',stats.overdueUnclaimed,'pink',()=>goTab('unclaimed')],
              ] as [string,any,any,()=>void][]).map(([label,value,color,onClick])=>(
                <button key={label} type="button" className="text-left hover:opacity-90" onClick={onClick}>
                  <StatCard label={label} value={value||0} color={color}/>
                </button>
              ))}
            </div>
            <button type="button" className="text-xs text-[#02357A] font-semibold" onClick={()=>setMoreStats(v=>!v)}>{moreStats?'Hide extra counts':'Show more counts'}</button>
            {moreStats&&(
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
              {([
                ['Received today',stats.receivedToday,'teal',()=>goQueue('in-process')],
                ['Reserved chambers',stats.chambersReserved,'indigo',()=>goTab('chambers')],
                ['Pending hospital intake',stats.pendingIntake,'amber',()=>goTab('intake')],
                ['Autopsies today',stats.autopsiesToday,'purple',()=>goTab('postmortem')],
                ['Autopsies pending',stats.autopsiesPending,'orange',()=>goTab('postmortem')],
                ['Specimens pending',stats.specimensPending,'teal',()=>goTab('reports')],
                ['Toxicology pending',stats.toxPending,'amber',()=>goTab('reports')],
                ['Reports pending',stats.reportsPending,'indigo',()=>goTab('postmortem')],
                ['Occupancy %',stats.occupancyPercent,'cyan',()=>goTab('chambers')],
                ['Temp alerts',stats.temperatureAlerts,'red',()=>nav('/mortuary/iot')],
                ['Equipment alerts',stats.equipmentAlerts,'pink',()=>nav('/mortuary/iot')],
                ['Billing outstanding',stats.billingOutstanding,'slate',()=>goTab('records')],
                ['Viewings today',stats.viewingsToday,'teal',()=>nav('/mortuary/chapel')],
                ['Chapel today',stats.chapelToday,'indigo',()=>nav('/mortuary/chapel')],
                ['Trips today',stats.tripsToday,'cyan',()=>nav('/mortuary/transport')],
                ['Burials today',stats.burialsToday,'green',()=>nav('/mortuary/cemetery')],
                ['Cremations today',stats.cremationsToday,'orange',()=>nav('/mortuary/crematorium')],
                ['Dispositions today',stats.dispositionsToday,'purple',()=>nav('/mortuary/final-disposition')],
              ] as [string,any,any,()=>void][]).map(([label,value,color,onClick])=>(
                <button key={label} type="button" className="text-left hover:opacity-90" onClick={onClick}>
                  <StatCard label={label} value={value||0} color={color}/>
                </button>
              ))}
            </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#02357A]">Work lanes</h3>
                <button className="text-xs text-[#02357A] underline" onClick={()=>goQueue('in-process')}>Open in-process register</button>
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {([['awaiting-id','Awaiting identification'],['awaiting-storage','Awaiting storage'],['autopsy','Autopsy / PM'],['ready-release','Ready to release'],['hold','Legal hold'],['released','Released']] as const).map(([q,title])=>{
                  const rows=laneBodies(q);
                  const n=laneCount(q);
                  const meta=QUEUE_META[q];
                  return(
                    <div key={q} className="card p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.cls}`}>{title}</span>
                        <button className="text-[11px] text-[#02357A] font-semibold" onClick={()=>goQueue(q)}>{n} →</button>
                      </div>
                      {rows.length===0&&<p className="text-xs text-slate-400 px-1 py-3">None in this lane</p>}
                      {rows.map((b:any)=>(
                        <button key={b.id} type="button" className="w-full text-left rounded-lg border border-slate-100 hover:border-[#015CA6] px-2 py-1.5" onClick={()=>nav(`/mortuary/bodies/${b.id}`)}>
                          <p className="text-sm font-medium text-slate-800 truncate">{b.full_name||'Unknown deceased'}</p>
                          <p className="text-[11px] text-slate-500">{b.body_number} · {b.chamber_number||'No chamber'} · {prettyMortuary(b.status)}</p>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
            {(slaRows||[]).length>0&&(
              <div className="card p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">Open SLA / escalations</h3>
                  <button className="text-xs underline" onClick={()=>goTab('analytics')}>Analytics</button>
                </div>
                {slaRows.slice(0,6).map((s:any)=>(
                  <button key={s.id} className="block w-full text-left text-xs px-2 py-1 rounded bg-pink-50 text-pink-800" onClick={()=>s.body_id&&nav(`/mortuary/bodies/${s.body_id}`)}>{s.case_number||s.body_number} · {s.policy_code} · {s.severity}</button>
                ))}
              </div>
            )}
            {(dash.iotAlerts||[]).length>0&&(
              <div className="card p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">IoT / equipment</h3>
                  <button className="text-xs underline" onClick={()=>nav('/mortuary/iot')}>Open IoT</button>
                </div>
                <div className="space-y-1">{dash.iotAlerts.slice(0,8).map((a:any)=>(
                  <button key={a.id} className={`block w-full text-left text-xs px-2 py-1 rounded ${a.severity==='CRITICAL'?'bg-red-50 text-red-800':'bg-amber-50 text-amber-800'}`} onClick={()=>nav('/mortuary/iot')}>{a.severity}: {a.message}</button>
                ))}</div>
              </div>
            )}
            {(dash.alerts||[]).length>0&&(
              <div className="card p-4">
                <h3 className="font-semibold mb-2">Alerts</h3>
                <div className="space-y-1">{dash.alerts.slice(0,12).map((a:any,i:number)=>(
                  <button key={i} className="block w-full text-left text-xs px-2 py-1 rounded bg-amber-50 text-amber-800" onClick={()=>{if(a.type==='PENDING_INTAKE')goTab('intake');else if(a.bodyId)nav(`/mortuary/bodies/${a.bodyId}`);}}>{a.message}</button>
                ))}</div>
              </div>
            )}
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="card p-4">
                <h3 className="font-semibold mb-2">Recent admissions</h3>
                <DataTable hideSearch data={dash.recentAdmissions||[]} emptyTitle="No admissions" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.id}`)}
                  columns={[{key:'case_number',label:'Case'},{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'queue',label:'Work status',render:(r:any)=><QueueBadge body={r}/>},{key:'received_at',label:'Received',render:(r:any)=>fmtDateTime(r.received_at)}]}/>
              </div>
              <div className="card p-4">
                <h3 className="font-semibold mb-2">Recent releases</h3>
                <DataTable hideSearch data={dash.recentReleases||[]} emptyTitle="No releases" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.body_id||r.id}`)}
                  columns={[{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'recipient_name',label:'Recipient'},{key:'released_at',label:'Released',render:(r:any)=>fmtDateTime(r.released_at)}]}/>
              </div>
            </div>
          </div>
        )}

        {tab==='intake'&&(
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Hospital deaths that are not yet a mortuary record. Register one to start identification and storage.</p>
            <div className="card overflow-hidden">
              <h3 className="font-semibold p-3">IPD deaths pending intake</h3>
              <DataTable hideSearch data={intakes.ipd||[]} emptyTitle="No pending IPD deaths"
                columns={[
                  {key:'ipd_number',label:'IPD No'},
                  {key:'full_name',label:'Name'},
                  {key:'uhid',label:'UHID'},
                  {key:'discharge_date',label:'Death / discharge',render:(r:any)=>fmtDateTime(r.discharge_date)},
                  {key:'id',label:'',render:(r:any)=>(
                    <button className="btn-primary text-xs" disabled={saving} onClick={async()=>{
                      setSaving(true);
                      try{const res=unwrap(await api.intakeMortuaryFromAdmission(r.id));notify(`✅ ${res.body_number||res.bodyNumber} registered`);nav(`/mortuary/bodies/${res.id}`);}
                      catch(e:any){notify('❌ '+(e.response?.data?.message||'Intake failed'));}
                      finally{setSaving(false);}
                    }}>Register</button>
                  )},
                ]}/>
            </div>
            <div className="card overflow-hidden">
              <h3 className="font-semibold p-3">Emergency DOA / brought-dead</h3>
              <DataTable hideSearch data={intakes.emergency||[]} emptyTitle="No pending emergency deaths"
                columns={[
                  {key:'ipd_number',label:'Case No'},
                  {key:'full_name',label:'Name'},
                  {key:'uhid',label:'UHID'},
                  {key:'discharge_type',label:'Arrival / type'},
                  {key:'id',label:'',render:(r:any)=>(
                    <button className="btn-primary text-xs" disabled={saving} onClick={async()=>{
                      setSaving(true);
                      try{const res=unwrap(await api.intakeMortuaryFromEmergency(r.id));notify(`✅ ${res.body_number||res.bodyNumber} registered`);nav(`/mortuary/bodies/${res.id}`);}
                      catch(e:any){notify('❌ '+(e.response?.data?.message||'Intake failed'));}
                      finally{setSaving(false);}
                    }}>Register</button>
                  )},
                ]}/>
            </div>
          </div>
        )}

        {tab==='register'&&(
          <form onSubmit={register} className="card p-4 space-y-4">
            <h3 className="font-semibold">Body information</h3>
            <PatientSearchInput selected={patient} onSelect={(p)=>{setPatient(p);setForm({...form,fullName:[p.firstName,p.lastName].filter(Boolean).join(' '),gender:p.gender||'',medicalRecordNumber:p.uhid||'',dateOfBirth:p.dateOfBirth||''});}} onClear={()=>setPatient(null)}/>
            <div className="grid md:grid-cols-3 gap-3">
              <div><label className="label">Full name *</label><Input required value={form.fullName||''} onChange={e=>setForm({...form,fullName:e.target.value})}/></div>
              <div><label className="label">Gender</label><Select value={form.gender||''} onChange={e=>setForm({...form,gender:e.target.value})}><option value="">—</option><option>MALE</option><option>FEMALE</option><option>OTHER</option></Select></div>
              <div><label className="label">National ID / Passport</label><Input value={form.nationalId||''} onChange={e=>setForm({...form,nationalId:e.target.value})}/></div>
              <div><label className="label">Date of birth</label><Input type="date" value={form.dateOfBirth||''} onChange={e=>setForm({...form,dateOfBirth:e.target.value})}/></div>
              <div><label className="label">Age</label><Input type="number" value={form.ageYears||''} onChange={e=>setForm({...form,ageYears:e.target.value})}/></div>
              <div><label className="label">MRN / UHID</label><Input value={form.medicalRecordNumber||''} onChange={e=>setForm({...form,medicalRecordNumber:e.target.value})}/></div>
              <div><label className="label">Date/time of death</label><Input type="datetime-local" value={form.dateOfDeath||''} onChange={e=>setForm({...form,dateOfDeath:e.target.value})}/></div>
              <div><label className="label">Place of death</label><Input value={form.placeOfDeath||''} onChange={e=>setForm({...form,placeOfDeath:e.target.value})}/></div>
              <div><label className="label">Death certificate No</label><Input value={form.deathCertificateNumber||''} onChange={e=>setForm({...form,deathCertificateNumber:e.target.value})}/></div>
              <div><label className="label">Death type</label><Select value={form.deathType||''} onChange={e=>setForm({...form,deathType:e.target.value})}>{opts('DEATH_TYPE').map((x:any)=><option key={x.code} value={x.code}>{x.label}</option>)}</Select></div>
              <div><label className="label">Source</label><Select value={form.source||''} onChange={e=>setForm({...form,source:e.target.value})}>{opts('BODY_SOURCE').map((x:any)=><option key={x.code} value={x.code}>{x.label}</option>)}</Select></div>
              <div><label className="label">Cause of death</label><Input value={form.causeOfDeath||''} onChange={e=>setForm({...form,causeOfDeath:e.target.value})}/></div>
            </div>
            <h3 className="font-semibold">Receiving information</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div><label className="label">Received at</label><Input type="datetime-local" value={form.receivedAt||''} onChange={e=>setForm({...form,receivedAt:e.target.value})}/></div>
              <div><label className="label">Brought by</label><Input value={form.broughtBy||''} onChange={e=>setForm({...form,broughtBy:e.target.value})}/></div>
              <div><label className="label">Relationship</label><Input value={form.broughtByRelationship||''} onChange={e=>setForm({...form,broughtByRelationship:e.target.value})}/></div>
              <div><label className="label">Receiving organization</label><Input value={form.receivingOrganization||''} onChange={e=>setForm({...form,receivingOrganization:e.target.value})}/></div>
              <div><label className="label">Police reference</label><Input value={form.policeReferenceNumber||''} onChange={e=>setForm({...form,policeReferenceNumber:e.target.value})}/></div>
              <div><label className="label">Referral number</label><Input value={form.referralNumber||''} onChange={e=>setForm({...form,referralNumber:e.target.value})}/></div>
              <div><label className="label">Condition</label><Select value={form.bodyCondition||''} onChange={e=>setForm({...form,bodyCondition:e.target.value})}>{opts('BODY_CONDITION').map((x:any)=><option key={x.code} value={x.code}>{x.label}</option>)}</Select></div>
              <div><label className="label">Chamber</label>
                <Select value={form.chamberId||''} onChange={e=>setForm({...form,chamberId:e.target.value})}>
                  <option value="">Assign later</option>
                  {chambers.filter((c:any)=>c.status!=='OCCUPIED'&&c.status!=='OUT_OF_SERVICE').map((c:any)=><option key={c.id} value={c.id}>{c.chamber_number} {c.rack_shelf_slot||''} ({c.status})</option>)}
                </Select>
              </div>
              <div className="flex items-end gap-2"><label className="text-sm"><input type="checkbox" checked={!!form.postMortemRequired} onChange={e=>setForm({...form,postMortemRequired:e.target.checked})}/> Post-mortem required</label></div>
              <div><label className="label">Religion / rites</label><Input value={form.religion||''} onChange={e=>setForm({...form,religion:e.target.value})}/></div>
              <div><label className="label">Weight (kg)</label><Input type="number" value={form.bodyWeightKg||''} onChange={e=>setForm({...form,bodyWeightKg:e.target.value})}/></div>
              <div><label className="label">Height (cm)</label><Input type="number" value={form.bodyHeightCm||''} onChange={e=>setForm({...form,bodyHeightCm:e.target.value})}/></div>
              <div><label className="label">Blood group</label><Input value={form.bloodGroup||''} onChange={e=>setForm({...form,bloodGroup:e.target.value})}/></div>
              <div className="flex items-end gap-2"><label className="text-sm"><input type="checkbox" checked={!!form.medicoLegal} onChange={e=>setForm({...form,medicoLegal:e.target.checked})}/> Medico-legal</label></div>
              <div className="flex items-end gap-2"><label className="text-sm"><input type="checkbox" checked={!!form.infectious} onChange={e=>setForm({...form,infectious:e.target.checked})}/> Infectious / isolation</label></div>
              <div className="flex items-end gap-2"><label className="text-sm"><input type="checkbox" checked={!!form.legalHold} onChange={e=>setForm({...form,legalHold:e.target.checked})}/> Legal hold</label></div>
            </div>
            {form.legalHold&&<Input placeholder="Legal hold reason" value={form.legalHoldReason||''} onChange={e=>setForm({...form,legalHoldReason:e.target.value})}/>}
            <h3 className="font-semibold">Next of kin</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div><label className="label">Name</label><Input value={form.nextOfKinName||''} onChange={e=>setForm({...form,nextOfKinName:e.target.value})}/></div>
              <div><label className="label">Relationship</label><Input value={form.nextOfKinRelationship||''} onChange={e=>setForm({...form,nextOfKinRelationship:e.target.value})}/></div>
              <div><label className="label">Phone</label><Input value={form.nextOfKinPhone||''} onChange={e=>setForm({...form,nextOfKinPhone:e.target.value})}/></div>
            </div>
            <div><label className="label">Visible injuries / marks</label><Textarea rows={2} value={form.visibleInjuries||''} onChange={e=>setForm({...form,visibleInjuries:e.target.value})}/></div>
            <div><label className="label">Personal belongings note</label><Textarea rows={2} value={form.personalBelongingsNote||''} onChange={e=>setForm({...form,personalBelongingsNote:e.target.value})}/></div>
            <div><label className="label">Notes</label><Textarea rows={2} value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
            <button className="btn-primary" disabled={saving}>{saving?'Saving…':'Register body'}</button>
          </form>
        )}

        {tab==='records'&&(
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {QUEUES.map(([id,label])=>(
                <button key={id} type="button" onClick={()=>goQueue(id)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${queue===id?'bg-[#02357A] text-white border-[#015CA6]':'bg-white border-slate-200 text-slate-600 hover:border-[#015CA6]'}`}>
                  {label} <span className={queue===id?'text-white/80':'text-slate-400'}>{queueCounts[id]||0}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">Default view is in-process so released and closed cases do not hide active work. Click a row to open the body workbench.</p>
            <div className="card overflow-hidden">
              <DataTable loading={loading} data={queuedBodies} emptyTitle="No bodies in this queue" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.id}`)}
                columns={[
                  {key:'case_number',label:'Case'},
                  {key:'body_number',label:'Body No'},
                  {key:'full_name',label:'Name'},
                  {key:'patient_uhid',label:'Patient'},
                  {key:'queue',label:'Work status',render:(r:any)=><QueueBadge body={r}/>},
                  {key:'status',label:'Status',render:(r:any)=><span className="text-[11px] text-slate-500">{prettyMortuary(r.status)}</span>},
                  {key:'legal_hold',label:'Hold',render:(r:any)=>r.legal_hold?<span className="text-[10px] font-bold text-red-700">HOLD</span>:''},
                  {key:'chamber_number',label:'Chamber'},
                  {key:'received_at',label:'Received',render:(r:any)=>fmtDateTime(r.received_at)},
                ]}/>
            </div>
          </div>
        )}

        {tab==='chambers'&&(
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-[#02357A]">Cold-storage map</h3>
                <p className="text-xs text-slate-500">Green is free. Red shows who is in the tray. Click a name to open that body.</p>
              </div>
              <button className="btn-primary text-sm" onClick={()=>setShowChamber(true)}>+ Chamber</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {([['all','All'],['free','Free'],['occupied','Occupied'],['offline','Offline']] as const).map(([id,label])=>(
                <button key={id} type="button" onClick={()=>setChamberFilter(id)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${chamberFilter===id?'bg-[#02357A] text-white border-[#015CA6]':'bg-white border-slate-200 text-slate-600'}`}>{label}</button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {chambers.filter((ch:any)=>{
                const occ=occupantsOf(ch,bodies);
                const tone=chamberTone(ch,occ).label;
                if(chamberFilter==='free') return tone==='Free';
                if(chamberFilter==='occupied') return tone==='Occupied';
                if(chamberFilter==='offline') return tone==='Offline';
                return true;
              }).map((ch:any)=>{
                const occ=occupantsOf(ch,bodies);
                const tone=chamberTone(ch,occ);
                const selected=tempChamber===ch.id;
                return(
                  <div key={ch.id} role="button" tabIndex={0} onClick={()=>{setTempChamber(ch.id);loadTemp(ch.id);}}
                    onKeyDown={(e)=>{if(e.key==='Enter'){setTempChamber(ch.id);loadTemp(ch.id);}}}
                    className={`text-left rounded-2xl border p-4 space-y-2 cursor-pointer ${tone.cls} ${selected?'ring-2 ring-[#02357A]':''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-lg font-bold text-slate-800">{ch.chamber_number}</p>
                        <p className="text-[11px] text-slate-500">{ch.mortuary_name}{ch.rack_shelf_slot?` · ${ch.rack_shelf_slot}`:''}</p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/80 border border-white">{tone.label}</span>
                    </div>
                    <p className="text-xs text-slate-600">{occ.length}/{ch.capacity||1} occupied{ch.temperature?` · ${ch.temperature}`:''}</p>
                    {occ.length>(ch.capacity||1)&&<p className="text-[11px] font-semibold text-red-700">Over capacity — move a body to a free tray</p>}
                    {occ.length===0
                      ? <p className="text-sm text-slate-500">Empty — available for a new body</p>
                      : occ.map((b:any)=>(
                        <button key={b.id} type="button" className="block w-full text-left rounded-lg bg-white/80 border border-white px-2 py-1.5 hover:border-[#015CA6]"
                          onClick={(e)=>{e.stopPropagation();nav(`/mortuary/bodies/${b.id}`);}}>
                          <p className="text-sm font-medium text-slate-800 truncate">{b.full_name||'Unknown deceased'}</p>
                          <p className="text-[11px] text-slate-500">{b.body_number} · {prettyMortuary(b.identification_status||b.status)}{b.legal_hold?' · HOLD':''}</p>
                        </button>
                      ))}
                  </div>
                );
              })}
              {chambers.length===0&&<p className="text-sm text-slate-400 col-span-full">No chambers yet. Add one to start storage.</p>}
            </div>
            <div className="card p-4 space-y-2">
              <h4 className="text-sm font-semibold">Temperature log</h4>
              <div className="flex flex-wrap gap-2">
                <Select value={tempChamber} onChange={e=>{setTempChamber(e.target.value);loadTemp(e.target.value);}}>
                  <option value="">Select chamber</option>
                  {chambers.map((c:any)=><option key={c.id} value={c.id}>{c.chamber_number}{occupantsOf(c,bodies)[0]?` — ${occupantsOf(c,bodies)[0].full_name}`:''}</option>)}
                </Select>
                <Input type="number" step="0.1" placeholder="°C" value={tempC} onChange={e=>setTempC(e.target.value)}/>
                <button className="btn-primary text-sm" disabled={!tempChamber||!tempC||saving} onClick={async()=>{
                  setSaving(true);
                  try{await api.addMortuaryChamberTemp(tempChamber,{temperatureC:tempC});setTempC('');notify('✅ Temperature recorded');await loadTemp(tempChamber);await loadChambers();}
                  catch(e:any){notify('❌ '+(e.response?.data?.message||'Could not log temperature'));}
                  finally{setSaving(false);}
                }}>Log reading</button>
              </div>
              <div className="text-xs space-y-1">{tempLogs.map((t:any)=><p key={t.id}>{fmtDateTime(t.recorded_at)} · {t.temperature_c}°C {t.notes?`· ${t.notes}`:''}</p>)}</div>
            </div>
          </div>
        )}

        {tab==='postmortem'&&(
          <div className="card overflow-hidden">
            <DataTable data={bodies.filter((b:any)=>b.post_mortem_required||b.status?.includes('POST_MORTEM')||b.status?.includes('AUTOPSY'))} emptyTitle="No post-mortem cases" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.id}/autopsy`)}
              columns={[{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'queue',label:'Work status',render:(r:any)=><QueueBadge body={r}/>},{key:'status',label:'Status',render:(r:any)=>prettyMortuary(r.status)},{key:'police_reference_number',label:'Police ref'}]}/>
          </div>
        )}

        {tab==='unclaimed'&&(
          <div className="card overflow-hidden">
            <p className="p-3 text-xs text-slate-500">Threshold: {settings.unclaimedDays||settings.unclaimed_days||30} days. Final disposition is never automatic.</p>
            <DataTable data={unclaimed} emptyTitle="No unclaimed bodies" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.id}`)}
              columns={[{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'days_held',label:'Days held'},{key:'status',label:'Status',render:(r:any)=>prettyMortuary(r.status)}]}/>
          </div>
        )}

        {tab==='releases'&&(
          <div className="card overflow-hidden">
            <DataTable data={bodies.filter((b:any)=>b.status==='RELEASED')} emptyTitle="No releases" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.id}`)}
              columns={[{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'released_at',label:'Released',render:(r:any)=>fmtDateTime(r.released_at)}]}/>
          </div>
        )}

        {tab==='transfers'&&(
          <div className="card overflow-hidden">
            <DataTable data={transferRows} emptyTitle="No transfers" onRowClick={(r:any)=>r.body_id&&nav(`/mortuary/bodies/${r.body_id}`)}
              columns={[{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'transfer_type',label:'Type'},{key:'destination',label:'Destination'},{key:'transferred_at',label:'When',render:(r:any)=>fmtDateTime(r.transferred_at)}]}/>
          </div>
        )}

        {tab==='belongings'&&(
          <div className="card overflow-hidden">
            <DataTable data={belongingRows} emptyTitle="No belongings recorded" onRowClick={(r:any)=>r.body_id&&nav(`/mortuary/bodies/${r.body_id}`)}
              columns={[{key:'body_number',label:'Body No'},{key:'full_name',label:'Name'},{key:'item_description',label:'Item'},{key:'quantity',label:'Qty'},{key:'returned_to',label:'Returned to'}]}/>
          </div>
        )}

        {tab==='mci'&&(
          <div className="card p-4 space-y-3">
            <h3 className="font-semibold">Mass casualty / disaster intake</h3>
            <div className="flex flex-wrap gap-2">
              <Input placeholder="Incident name" value={mciName} onChange={e=>setMciName(e.target.value)}/>
              <Input type="number" value={mciCount} onChange={e=>setMciCount(+e.target.value)}/>
              <button className="btn-primary text-sm" onClick={async()=>{
                try{
                  const inc=unwrap(await api.createMortuaryMci({name:mciName||'Mass casualty'}));
                  const items=Array.from({length:mciCount},(_,i)=>({fullName:`Unidentified ${i+1}`}));
                  await api.bulkMortuaryMci(inc.id,items);
                  notify(`✅ ${mciCount} temporary body IDs created`);
                  setMci(unwrap(await api.getMortuaryMci())||[]);
                }catch(e:any){notify('❌ '+(e.response?.data?.message||'MCI intake failed'));}
              }}>Open incident + bulk register</button>
            </div>
            <DataTable data={mci} emptyTitle="No MCI incidents"
              columns={[{key:'incident_code',label:'Code'},{key:'name',label:'Name'},{key:'body_count',label:'Bodies'},{key:'started_at',label:'Started',render:(r:any)=>fmtDateTime(r.started_at)}]}/>
          </div>
        )}

        {tab==='reports'&&(
          <div className="card p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <Select value={reportKind} onChange={e=>setReportKind(e.target.value)}>
                <option value="daily-register">Daily register</option>
                <option value="in-mortuary">Currently in mortuary</option>
                <option value="received">Bodies received</option>
                <option value="released">Bodies released</option>
                <option value="unclaimed">Unclaimed bodies</option>
                <option value="movements">Movement register</option>
                <option value="post-mortem">Post-mortem register</option>
                <option value="handover">Handover register</option>
                <option value="belongings">Belongings register</option>
                <option value="transfers">Transfer register</option>
                <option value="charges">Mortuary charges</option>
                <option value="legal-hold">Legal hold</option>
                <option value="autopsy">Autopsy register</option>
                <option value="specimens">Specimen register</option>
                <option value="custody">Chain of custody</option>
                <option value="evidence">Evidence register</option>
                <option value="histopathology-pending">Histopathology pending</option>
                <option value="toxicology-pending">Toxicology pending</option>
                <option value="iot-temperature">IoT temperature</option>
                <option value="iot-excursion">Temperature excursions</option>
                <option value="calibration">Calibration due</option>
                <option value="equipment-incident">Equipment incidents</option>
                <option value="billing">Mortuary billing events</option>
                <option value="mass-casualty">Mass casualty</option>
                <option value="chapel">Chapel bookings</option>
                <option value="transport">Transport trips</option>
                <option value="cemetery">Burials</option>
                <option value="crematorium">Cremations</option>
                <option value="final-disposition">Final disposition</option>
                <option value="occupancy">Occupancy</option>
                <option value="death-source">Death source</option>
                <option value="cause-of-death">Cause of death</option>
                <option value="monthly">Monthly statistics</option>
              </Select>
              <button className="btn-secondary text-sm" onClick={loadReport}>Refresh</button>
              <button className="btn-secondary text-sm" onClick={()=>downloadCSV(reportRows,`mortuary-${reportKind}.csv`)}>Export CSV</button>
              <button className="btn-secondary text-sm" onClick={()=>window.print()}>Print / PDF</button>
            </div>
            <DataTable data={reportRows} emptyTitle="No report rows" onRowClick={(r:any)=>(r.body_id||r.id)&&nav(`/mortuary/bodies/${r.body_id||r.id}`)}
              columns={reportColumns(reportRows)}/>
          </div>
        )}

        {tab==='search'&&(
          <div className="card overflow-hidden">
            <DataTable loading={loading} data={searchRows} emptyTitle="No matching cases" onRowClick={(r:any)=>nav(`/mortuary/bodies/${r.id}`)}
              columns={[
                {key:'case_number',label:'Case'},
                {key:'body_number',label:'Body No'},
                {key:'full_name',label:'Name'},
                {key:'queue',label:'Work status',render:(r:any)=><QueueBadge body={r}/>},
                {key:'identification_status',label:'ID',render:(r:any)=>prettyMortuary(r.identification_status)},
                {key:'police_reference_number',label:'Police / MLC'},
                {key:'chamber_number',label:'Chamber'},
                {key:'legal_hold',label:'Hold',render:(r:any)=>r.legal_hold?<span className="text-[10px] font-bold text-red-700">HOLD</span>:''},
              ]}/>
          </div>
        )}

        {tab==='analytics'&&(
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Bodies" value={exec?.bodies||0} color="blue"/>
              <StatCard label="In mortuary" value={exec?.in_mortuary||0} color="teal"/>
              <StatCard label="Avg storage days" value={Math.round(+(exec?.avg_storage_days||0))} color="amber"/>
              <StatCard label="Unidentified" value={exec?.unidentified||0} color="orange"/>
              <StatCard label="MLC / hold" value={exec?.mlc||0} color="red"/>
              <StatCard label="Chapel this month" value={exec?.chapel_month||0} color="indigo"/>
              <StatCard label="Burials this month" value={exec?.burials_month||0} color="green"/>
              <StatCard label="Cremations this month" value={exec?.cremations_month||0} color="purple"/>
              <StatCard label="Open SLA" value={exec?.open_sla||0} color="pink"/>
              <StatCard label="Charge events" value={exec?.revenue||0} color="slate"/>
            </div>
            <div className="card overflow-hidden">
              <h3 className="font-semibold p-3">Open SLA / escalations</h3>
              <DataTable data={slaRows} emptyTitle="No open SLA breaches" onRowClick={(r:any)=>r.body_id&&nav(`/mortuary/bodies/${r.body_id}`)}
                columns={[{key:'case_number',label:'Case'},{key:'body_number',label:'Body'},{key:'policy_code',label:'Policy'},{key:'severity',label:'Severity'},{key:'detail',label:'Detail'}]}/>
            </div>
          </div>
        )}

        {tab==='masters'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-4 space-y-3">
              <h3 className="font-semibold">Policy & fees</h3>
              {[
                ['unclaimed_days','Unclaimed days'],
                ['storage_daily_fee','Storage fee / day'],
                ['release_fee','Release fee'],
                ['embalming_fee','Embalming fee'],
                ['viewing_fee','Viewing fee'],
              ].map(([key,label])=>(
                <div key={key} className="flex gap-2 items-center">
                  <label className="text-xs w-36">{label}</label>
                  <Input type="number" value={settings[key]??''} onChange={e=>setSettings({...settings,[key]:e.target.value})}/>
                  <button className="btn-secondary text-xs" onClick={async()=>{await api.saveMortuarySetting({key,value:settings[key]});notify(`✅ ${label} saved`);}}>Save</button>
                </div>
              ))}
            </div>
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Lookups</h3>
              {Object.keys(lookups).map(t=>(
                <p key={t} className="text-xs mb-1"><b>{t}</b>: {(lookups[t]||[]).map((x:any)=>x.label).join(', ')}</p>
              ))}
              <LegalRules/>
            </div>
          </div>
        )}
      </div>

      <SlidePanel isOpen={showChamber} onClose={()=>setShowChamber(false)} title="Chamber">
        <form onSubmit={saveChamber} className="space-y-3">
          <div><label className="label">Mortuary</label>
            <Select required value={chamberForm.mortuaryId||mortuaries[0]?.id||''} onChange={e=>setChamberForm({...chamberForm,mortuaryId:e.target.value})}>
              {mortuaries.map((m:any)=><option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          </div>
          <div><label className="label">Chamber number</label><Input required value={chamberForm.chamberNumber} onChange={e=>setChamberForm({...chamberForm,chamberNumber:e.target.value})}/></div>
          <div><label className="label">Rack / shelf / slot</label><Input value={chamberForm.rackShelfSlot} onChange={e=>setChamberForm({...chamberForm,rackShelfSlot:e.target.value})}/></div>
          <div><label className="label">Capacity</label><Input type="number" value={chamberForm.capacity} onChange={e=>setChamberForm({...chamberForm,capacity:e.target.value})}/></div>
          <div><label className="label">Temperature</label><Input value={chamberForm.temperature} onChange={e=>setChamberForm({...chamberForm,temperature:e.target.value})}/></div>
          <div><label className="label">Status</label>
            <Select value={chamberForm.status} onChange={e=>setChamberForm({...chamberForm,status:e.target.value})}>
              {['AVAILABLE','OCCUPIED','MAINTENANCE','RESERVED','OUT_OF_SERVICE'].map(s=><option key={s}>{s}</option>)}
            </Select>
          </div>
          <button className="btn-primary w-full" disabled={saving}>{saving?'Saving…':'Save chamber'}</button>
        </form>
      </SlidePanel>
    </Layout>
  );
}

function LegalRules(){
  const [rows,setRows]=useState<any[]>([]);
  const [form,setForm]=useState<any>({ruleKey:'block_release_if_unpaid',ruleValue:'false'});
  useEffect(()=>{api.getMortuaryLegalRules().then(r=>setRows(unwrap(r)||[])).catch(()=>null);},[]);
  return(
    <div className="pt-3 border-t space-y-2">
      <h4 className="text-sm font-semibold">Facility legal rules</h4>
      <p className="text-[11px] text-slate-500">Configuration only — no country statute is hard-coded.</p>
      {(rows||[]).map((r:any)=><p key={r.id} className="text-xs">{r.rule_key} = {r.rule_value}</p>)}
      <Input placeholder="Rule key" value={form.ruleKey} onChange={e=>setForm({...form,ruleKey:e.target.value})}/>
      <Input placeholder="Rule value" value={form.ruleValue} onChange={e=>setForm({...form,ruleValue:e.target.value})}/>
      <button className="btn-secondary text-xs" onClick={async()=>{
        await api.saveMortuaryLegalRule(form);
        setRows(unwrap(await api.getMortuaryLegalRules())||[]);
      }}>Save rule</button>
    </div>
  );
}
